/**
 * lib/anomalies.ts
 * ------------------------------------------------------------------
 * THE SECOND LOOK.
 *
 * Not errors. The books balance, the controls reconcile, and every one of
 * these may be entirely proper. These are the patterns an external auditor
 * charges to look for and a bursar has no time to: the same amount received
 * from the same pupil twice in a day, a journal dated three months before it
 * was typed, a gap in a receipt sequence, money sitting unidentified for a
 * term.
 *
 * A LIST OF QUESTIONS, NOT ACCUSATIONS. Every finding says what would make
 * it innocent, and says it up front. A screen that reads as an accusation
 * gets argued with; one that reads as a question gets answered.
 *
 * A CONTROL THAT CRIES WOLF IS SWITCHED OFF WITHIN A MONTH. The thresholds
 * are set where a reasonable person would actually want to look, they are
 * declared in one place, and the screen states them — a bursar who cannot
 * see the threshold cannot tell whether silence means "nothing found" or
 * "nothing looked for".
 *
 * PROVENANCE COMES FIRST, and it is the correction that makes the whole
 * thing usable. A record created by a migration was never going to carry a
 * human trail, and flagging seventy of them buries the one that matters. So
 * imported records are not excused and not hidden — they are reported ONCE,
 * as a cluster, saying what they are: "these N carry no approval trail
 * because they were migrated, so this system cannot evidence who approved
 * them." That is a real limitation the school should know about, stated
 * once, rather than the same sentence seventy times pretending to be
 * seventy separate frauds.
 *
 * WHAT CANNOT BE CHECKED YET is declared rather than omitted, because a page
 * that quietly leaves a check out implies a clean bill of health it has not
 * earned. Suppliers and Vouchers arrived in September 2026 and brought the
 * segregation-of-duties and banking checks with them; what remains waits on
 * purchase orders, the shop and petty cash.
 *
 * PURE. No Firestore, no React.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';

const money = (n: number) =>
  `P${Math.abs(round2(n)).toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dayOf = (s?: string) => String(s ?? '').slice(0, 10);

export type Severity = 'high' | 'medium' | 'low';

/**
 * Who or what created a record.
 *
 *   person    — a human used the system. Every check applies.
 *   imported  — a migration or seed. There is no approval history to judge,
 *               and there never was.
 *   automated — a trigger raised it. The authorisation lives on the thing
 *               that caused it, not on the record itself.
 */
export type Provenance = 'person' | 'imported' | 'automated';

export type CheckKey =
  | 'sod-single-hand'
  | 'bank-shared-account'
  | 'bank-changed-before-payment'
  | 'bank-unverified'
  | 'duplicate-receipt'
  | 'round-sum'
  | 'sequence-gap'
  | 'backdated-journal'
  | 'out-of-hours'
  | 'large-credit-note'
  | 'stale-unidentified'
  | 'long-standing-credit'
  | 'no-approval-trail';

export const CHECK_LABEL: Record<CheckKey, string> = {
  'sod-single-hand': 'One person moved a voucher on their own',
  'bank-shared-account': 'Two suppliers, one bank account',
  'bank-changed-before-payment': 'Bank details changed shortly before payment',
  'bank-unverified': 'Paid to bank details nobody verified',
  'duplicate-receipt': 'Possible duplicate receipt',
  'round-sum': 'Round-sum amount',
  'sequence-gap': 'Gap in a document sequence',
  'backdated-journal': 'Journal dated well before it was posted',
  'out-of-hours': 'Posted outside working hours',
  'large-credit-note': 'Large reduction with no money received',
  'stale-unidentified': 'Money unidentified for a long time',
  'long-standing-credit': 'Pupil in credit for a long time',
  'no-approval-trail': 'Records with no human approval trail',
};

export interface Finding {
  /** Stable across runs, so a finding is recognised rather than reappearing
   *  as new every time the screen is opened. */
  id: string;
  check: CheckKey;
  severity: Severity;
  title: string;
  detail: string;
  /** What would make it innocent. Every finding has an innocent
   *  explanation; saying it is what stops this reading as an accusation. */
  innocentIf: string;
  reference?: string;
  date?: string;
  amount?: number;
  provenance?: Provenance;
  /** Set on a CLUSTER — how many individual findings it stands for. */
  count?: number;
  members?: Finding[];
}

/** Thresholds in one place, so the school can be told what they are. */
export interface AnomalyThresholds {
  /** A receipt at or above this is worth a second look on its own. */
  materialAmount: number;
  /** Round-sum flagged at or above this, and only on exact thousands. */
  roundSumFrom: number;
  /** Days within which the same pupil and amount looks like a double receipt. */
  duplicateDays: number;
  /** A credit note or write-off at or above this. */
  largeCreditFrom: number;
  /** A journal dated this many days before it was posted. */
  backdateDays: number;
  /** Days a payment may sit unidentified before it is worth asking about. */
  unidentifiedDays: number;
  /** Days a pupil may sit in credit before somebody should look. */
  creditDays: number;
  /** Days between a banking change and a payment that make them related. */
  bankChangeDays: number;
  /** Work starts and ends. Posting outside these is not wrong, only worth
   *  noticing — a receipt keyed at 03:00 usually has an explanation. */
  workStartHour: number;
  workEndHour: number;
}

export const DEFAULT_THRESHOLDS: AnomalyThresholds = {
  materialAmount: 20000,
  roundSumFrom: 10000,
  duplicateDays: 2,
  largeCreditFrom: 5000,
  backdateDays: 45,
  unidentifiedDays: 30,
  creditDays: 120,
  bankChangeDays: 30,
  workStartHour: 6,
  workEndHour: 19,
};

/* --------------------------- what it reads --------------------------- */

export interface AnomalyPayment {
  id: string;
  receiptNo?: string;
  studentId?: string;
  studentName?: string;
  amount?: number;
  method?: string;
  date?: string;
  createdAt?: string;
  createdBy?: string;
  isReversal?: boolean;
}

export interface AnomalyInvoice {
  id: string;
  invoiceNo?: string;
  studentName?: string;
  status?: string;
  balance?: number;
  issuedAt?: string;
}

export interface AnomalyAdjustment {
  id: string;
  adjustmentNo?: string;
  kind?: string;
  studentName?: string;
  amount?: number;
  reason?: string;
  date?: string;
  createdBy?: string;
}

export interface AnomalyJournal {
  id: string;
  journalNo?: string;
  date?: string;
  postedAt?: string;
  postedBy?: string;
  narration?: string;
  status?: string;
  totalDr?: number;
}

export interface AnomalyUnidentified {
  id: string;
  reference?: string;
  amount?: number;
  receivedAt?: string;
  status?: string;
}

export interface AnomalyVoucher {
  id: string;
  voucherNo?: string;
  payeeName?: string;
  supplierId?: string;
  currentStatus?: string;
  total?: number;
  preparedBy?: string;
  checkedBy?: string;
  approvedBy?: string;
  paidAt?: string;
  method?: string;
  payeeAtPayment?: { bankVerified?: boolean };
}

export interface AnomalySupplier {
  id: string;
  name: string;
  bank?: { accountNumber?: string };
  bankVerifiedAt?: string;
  bankHistory?: { at: string }[];
}

export interface AnomalySources {
  payments: AnomalyPayment[];
  vouchers?: AnomalyVoucher[];
  suppliers?: AnomalySupplier[];
  invoices: AnomalyInvoice[];
  adjustments: AnomalyAdjustment[];
  journals: AnomalyJournal[];
  unidentified?: AnomalyUnidentified[];
  thresholds?: Partial<AnomalyThresholds>;
  /** Value the checks as at this moment (default: now). */
  asOf?: Date;
}

/** A record with no creator is a migration, not a person acting anonymously. */
export function provenanceOf(createdBy?: string): Provenance {
  const who = String(createdBy ?? '').trim().toLowerCase();
  if (!who || who === 'import' || who === 'migration' || who === 'seed') return 'imported';
  if (who === 'system' || who === 'trigger') return 'automated';
  return 'person';
}

const daysBetween = (a: string, b: string): number =>
  Math.round((Date.parse(b) - Date.parse(a)) / 86400000);

/* ------------------------------ the checks ------------------------------ */

export function findAnomalies(src: AnomalySources): Finding[] {
  const t = { ...DEFAULT_THRESHOLDS, ...(src.thresholds ?? {}) };
  const asOf = src.asOf ?? new Date();
  const today = asOf.toISOString().slice(0, 10);
  const out: Finding[] = [];

  /* ---- one person moved a voucher on their own ----
     The distinct-actor guard refuses this at the point of approval, so a
     finding here means a voucher that predates the control, was migrated, or
     was written by something that bypassed the callable. All three are worth
     knowing about and none of them are visible any other way. */
  for (const vo of src.vouchers ?? []) {
    if (!['approved', 'paid'].includes(String(vo.currentStatus))) continue;
    const hands = new Set([vo.preparedBy, vo.checkedBy, vo.approvedBy].filter(Boolean));
    if (hands.size >= 2) continue;
    out.push({
      id: `sod-${vo.id}`,
      check: 'sod-single-hand',
      severity: (Number(vo.total) || 0) >= t.materialAmount ? 'high' : 'medium',
      title: `${vo.voucherNo ?? vo.id} was raised and approved by one person`,
      detail: `${money(Number(vo.total) || 0)} to ${vo.payeeName ?? 'a payee'}.`,
      innocentIf: 'The voucher predates the approval chain, or was migrated from the '
        + 'previous system, in which case there was never a second pair of hands to record.',
      reference: vo.voucherNo,
      amount: Number(vo.total) || 0,
      provenance: provenanceOf(vo.preparedBy),
    });
  }

  /* ---- two suppliers, one bank account ----
     Occasionally a parent company. Usually one supplier entered twice, and
     sometimes an account belonging to somebody who works here attached to a
     supplier nobody has met. Reported once per account, not per supplier. */
  const byAccount = new Map<string, AnomalySupplier[]>();
  for (const sp of src.suppliers ?? []) {
    const num = String(sp.bank?.accountNumber ?? '').replace(/[^0-9]/g, '');
    if (!num) continue;
    byAccount.set(num, [...(byAccount.get(num) ?? []), sp]);
  }
  for (const [num, list] of byAccount) {
    if (list.length < 2) continue;
    out.push({
      id: `shared-${num}`,
      check: 'bank-shared-account',
      severity: 'high',
      title: `${list.length} suppliers share one bank account`,
      detail: `${list.map((x) => x.name).join(', ')} - account ${num}.`,
      innocentIf: 'A parent company, or a sole trader operating under more than one name.',
      reference: num,
    });
  }

  /* ---- banking changed shortly before a payment ----
     The shape of supplier fraud: change the destination, then wait for the
     next genuine invoice. The voucher control refuses a payment where the
     details moved AFTER approval; this catches the wider case where they
     moved shortly before the whole thing started. */
  const supplierById = new Map((src.suppliers ?? []).map((sp) => [sp.id, sp]));
  for (const vo of src.vouchers ?? []) {
    if (!vo.paidAt || !vo.supplierId) continue;
    const sp = supplierById.get(vo.supplierId);
    const history = sp?.bankHistory ?? [];
    if (!history.length) continue;
    const lastChange = history[history.length - 1].at;
    const gap = daysBetween(dayOf(lastChange), dayOf(vo.paidAt));
    if (Number.isNaN(gap) || gap < 0 || gap > t.bankChangeDays) continue;
    out.push({
      id: `bankchange-${vo.id}`,
      check: 'bank-changed-before-payment',
      severity: 'high',
      title: `Banking for ${vo.payeeName ?? 'a supplier'} changed ${gap} day(s) before they were paid`,
      detail: `${vo.voucherNo ?? vo.id}, ${money(Number(vo.total) || 0)}, paid ${dayOf(vo.paidAt)}. `
        + `Banking last changed ${dayOf(lastChange)}.`,
      innocentIf: 'The supplier genuinely changed banks, and somebody confirmed it against '
        + 'the number on the contract rather than the message that asked for the change.',
      reference: vo.voucherNo,
      date: dayOf(vo.paidAt),
      amount: Number(vo.total) || 0,
    });
  }

  /* ---- paid into details nobody confirmed ---- */
  for (const vo of src.vouchers ?? []) {
    if (!vo.paidAt || vo.method !== 'eft') continue;
    const verified = vo.payeeAtPayment?.bankVerified
      ?? (vo.supplierId ? Boolean(supplierById.get(vo.supplierId)?.bankVerifiedAt) : true);
    if (verified) continue;
    if ((Number(vo.total) || 0) < t.materialAmount) continue;
    out.push({
      id: `unverified-${vo.id}`,
      check: 'bank-unverified',
      severity: 'medium',
      title: `${money(Number(vo.total) || 0)} paid to ${vo.payeeName ?? 'a supplier'} without verified banking`,
      detail: `${vo.voucherNo ?? vo.id}, paid ${dayOf(vo.paidAt)}.`,
      innocentIf: 'The details were confirmed outside the system and nobody recorded it, '
        + 'which is worth fixing on the supplier record either way.',
      reference: vo.voucherNo,
      date: dayOf(vo.paidAt),
      amount: Number(vo.total) || 0,
    });
  }

  /* ---- the same money twice ----
     Two receipts for the same pupil and the same amount within a couple of
     days. Usually a parent paying twice by mistake, or a receipt captured
     twice — either way somebody should look, because one of them is either
     a refund owed or a receipt that does not exist. */
  const live = src.payments.filter((p) => !p.isReversal);
  const seen = new Map<string, AnomalyPayment[]>();
  for (const p of live) {
    const key = `${p.studentId ?? ''}|${round2(Number(p.amount) || 0)}`;
    seen.set(key, [...(seen.get(key) ?? []), p]);
  }
  for (const group of seen.values()) {
    if (group.length < 2) continue;
    const sorted = [...group].sort((a, b) => dayOf(a.date).localeCompare(dayOf(b.date)));
    for (let i = 1; i < sorted.length; i += 1) {
      const gap = daysBetween(dayOf(sorted[i - 1].date), dayOf(sorted[i].date));
      if (Number.isNaN(gap) || gap > t.duplicateDays) continue;
      const p = sorted[i];
      out.push({
        id: `dup-${sorted[i - 1].id}-${p.id}`,
        check: 'duplicate-receipt',
        severity: (Number(p.amount) || 0) >= t.materialAmount ? 'high' : 'medium',
        title: `${money(Number(p.amount) || 0)} received twice from ${p.studentName ?? 'the same pupil'}`,
        detail: `${sorted[i - 1].receiptNo ?? sorted[i - 1].id} on ${dayOf(sorted[i - 1].date)} and `
          + `${p.receiptNo ?? p.id} on ${dayOf(p.date)}, ${gap === 0 ? 'the same day' : `${gap} day(s) apart`}.`,
        innocentIf: 'The family genuinely paid twice — for two terms, or for two children '
          + 'billed the same amount.',
        reference: p.receiptNo,
        date: dayOf(p.date),
        amount: Number(p.amount) || 0,
        provenance: provenanceOf(p.createdBy),
      });
    }
  }

  /* ---- round sums ----
     An exact thousand is what a person types when they are estimating. On a
     school receipt it is usually genuine; on a large one it is worth asking
     whether it was reconciled to a bank line. */
  for (const p of live) {
    const amt = round2(Number(p.amount) || 0);
    if (amt < t.roundSumFrom || amt % 1000 !== 0) continue;
    out.push({
      id: `round-${p.id}`,
      check: 'round-sum',
      severity: 'low',
      title: `${money(amt)} — an exact thousand`,
      detail: `${p.receiptNo ?? p.id} from ${p.studentName ?? 'a pupil'} on ${dayOf(p.date)}.`,
      innocentIf: 'The family paid a round amount, which most do.',
      reference: p.receiptNo,
      date: dayOf(p.date),
      amount: amt,
      provenance: provenanceOf(p.createdBy),
    });
  }

  /* ---- gaps in a sequence ----
     A gapless series is the point of a receipt book. A missing number is
     either a document that was never issued or one that was removed. */
  out.push(...sequenceGaps(
    src.payments.map((p) => p.receiptNo).filter(Boolean) as string[],
    'receipt',
  ));
  out.push(...sequenceGaps(
    src.invoices.filter((i) => i.status !== 'void').map((i) => i.invoiceNo).filter(Boolean) as string[],
    'invoice',
  ));

  /* ---- a journal dated long before it was typed ----
     Perfectly normal at a year end and worth a question at any other time:
     a journal dated into a period somebody has already reported on changes
     a figure that has been read. */
  for (const j of src.journals) {
    if (j.status === 'reversed') continue;
    const d = dayOf(j.date);
    const posted = dayOf(j.postedAt);
    if (!d || !posted) continue;
    const gap = daysBetween(d, posted);
    if (Number.isNaN(gap) || gap < t.backdateDays) continue;
    out.push({
      id: `backdate-${j.id}`,
      check: 'backdated-journal',
      severity: gap > 180 ? 'high' : 'medium',
      title: `${j.journalNo ?? j.id} dated ${gap} days before it was posted`,
      detail: `Dated ${d}, posted ${posted}. ${j.narration ?? ''}`.trim(),
      innocentIf: 'A year-end adjustment, or a correction to a month that was still open.',
      reference: j.journalNo,
      date: d,
      amount: Number(j.totalDr) || 0,
      provenance: provenanceOf(j.postedBy),
    });
  }

  /* ---- posted outside working hours ----
     Not wrong. Worth noticing: a receipt keyed at three in the morning
     usually has an explanation, and it is better to have asked. */
  for (const j of src.journals) {
    if (j.status === 'reversed' || !j.postedAt) continue;
    const hour = new Date(j.postedAt).getUTCHours();
    if (Number.isNaN(hour) || (hour >= t.workStartHour && hour < t.workEndHour)) continue;
    out.push({
      id: `hours-${j.id}`,
      check: 'out-of-hours',
      severity: 'low',
      title: `${j.journalNo ?? j.id} posted at ${String(hour).padStart(2, '0')}:00`,
      detail: `${j.narration ?? 'Journal'} posted ${dayOf(j.postedAt)}.`,
      innocentIf: 'Somebody working late at a month end, or a scheduled job.',
      reference: j.journalNo,
      date: dayOf(j.postedAt),
      provenance: provenanceOf(j.postedBy),
    });
  }

  /* ---- a large reduction with no money received ----
     A credit note reduces what a family owes without anybody paying. That
     is exactly what it is for, and exactly why it is the instrument to
     look at. */
  for (const a of src.adjustments) {
    const amt = round2(Number(a.amount) || 0);
    if (amt < t.largeCreditFrom) continue;
    out.push({
      id: `credit-${a.id}`,
      check: 'large-credit-note',
      severity: amt >= t.materialAmount ? 'high' : 'medium',
      title: `${money(amt)} taken off ${a.studentName ?? 'a pupil'}'s account`,
      detail: `${a.adjustmentNo ?? a.id} on ${dayOf(a.date)}: ${a.reason ?? 'no reason recorded'}.`,
      innocentIf: 'A bursary or a concession the school agreed, with the approval on file.',
      reference: a.adjustmentNo,
      date: dayOf(a.date),
      amount: amt,
      provenance: provenanceOf(a.createdBy),
    });
  }

  /* ---- money nobody has claimed ----
     A bank credit that cannot be matched to a pupil is somebody's fees
     sitting in suspense. The longer it sits, the more likely a family is
     being chased for money they have already paid. */
  for (const u of src.unidentified ?? []) {
    if (u.status && u.status !== 'open' && u.status !== 'pending') continue;
    const age = daysBetween(dayOf(u.receivedAt), today);
    if (Number.isNaN(age) || age < t.unidentifiedDays) continue;
    out.push({
      id: `unid-${u.id}`,
      check: 'stale-unidentified',
      severity: age > 90 ? 'high' : 'medium',
      title: `${money(Number(u.amount) || 0)} unidentified for ${age} days`,
      detail: `Received ${dayOf(u.receivedAt)}${u.reference ? `, reference "${u.reference}"` : ''}.`,
      innocentIf: 'Nothing — this one always needs allocating. The question is only whose it is.',
      reference: u.reference,
      date: dayOf(u.receivedAt),
      amount: Number(u.amount) || 0,
    });
  }

  /* ---- a pupil in credit for a long time ----
     Money the school is holding. Usually an overpayment nobody refunded,
     occasionally a receipt on the wrong account. */
  for (const i of src.invoices) {
    const bal = round2(Number(i.balance) || 0);
    if (i.status === 'void' || bal >= 0) continue;
    const age = daysBetween(dayOf(i.issuedAt), today);
    if (Number.isNaN(age) || age < t.creditDays) continue;
    out.push({
      id: `credit-age-${i.id}`,
      check: 'long-standing-credit',
      severity: Math.abs(bal) >= t.materialAmount ? 'medium' : 'low',
      title: `${i.studentName ?? 'A pupil'} has been ${money(bal)} in credit for ${age} days`,
      detail: `${i.invoiceNo ?? i.id}, issued ${dayOf(i.issuedAt)}.`,
      innocentIf: 'Fees paid a year ahead, which some families do.',
      reference: i.invoiceNo,
      date: dayOf(i.issuedAt),
      amount: Math.abs(bal),
    });
  }

  return out;
}

/**
 * Gaps in a numbered series.
 *
 * References look like PREFIX/YEAR/0001. The prefix and year are the series;
 * the trailing number is what must be gapless. A reference that does not fit
 * that shape is skipped rather than guessed at — inventing a sequence from
 * free text produces gaps that were never there.
 */
export function sequenceGaps(refs: string[], kind: string): Finding[] {
  const series = new Map<string, number[]>();
  for (const ref of refs) {
    const m = /^(.*[^0-9])(\d+)$/.exec(String(ref).trim());
    if (!m) continue;
    const [, prefix, num] = m;
    series.set(prefix, [...(series.get(prefix) ?? []), Number(num)]);
  }

  const out: Finding[] = [];
  for (const [prefix, nums] of series) {
    const sorted = [...new Set(nums)].sort((a, b) => a - b);
    if (sorted.length < 2) continue;
    for (let i = 1; i < sorted.length; i += 1) {
      const from = sorted[i - 1];
      const to = sorted[i];
      if (to - from <= 1) continue;
      const missing = to - from - 1;
      out.push({
        id: `gap-${prefix}${from}-${to}`,
        check: 'sequence-gap',
        severity: missing > 5 ? 'high' : 'medium',
        title: `${missing} missing ${kind} number${missing === 1 ? '' : 's'} after ${prefix}${from}`,
        detail: `The series runs ${prefix}${from} then ${prefix}${to}.`,
        innocentIf: 'A document was voided and removed, or the series was started mid-year '
          + 'after numbers had already been used on paper.',
        reference: `${prefix}${from}`,
      });
    }
  }
  return out;
}

/**
 * Collapse a run of identical findings about migrated records into one.
 *
 * THE CORRECTION THAT MAKES THE SCREEN USABLE. Seventy findings saying "this
 * record carries no approval trail" is seventy false positives burying the
 * one that matters. Reported once, as what it is: a limitation of the
 * migration, not seventy separate frauds.
 */
export function clusterFindings(findings: Finding[], minCluster = 4): Finding[] {
  const imported = findings.filter((f) => f.provenance === 'imported');
  if (imported.length < minCluster) return findings;

  const rest = findings.filter((f) => f.provenance !== 'imported');
  const total = round2(imported.reduce((a, f) => a + (f.amount ?? 0), 0));

  return [
    {
      id: 'cluster-imported',
      check: 'no-approval-trail',
      severity: 'low',
      title: `${imported.length} records carry no human approval trail`,
      detail: `They were migrated into the system rather than entered through it`
        + `${total ? `, and come to ${money(total)}` : ''}. This system cannot evidence `
        + 'who approved them, because there was never an approval to record.',
      innocentIf: 'Nothing to explain — it is a limitation of the migration, stated once '
        + 'rather than repeated against every record.',
      count: imported.length,
      members: imported,
      provenance: 'imported',
    },
    ...rest,
  ];
}

export interface AnomalySummary {
  total: number;
  high: number;
  medium: number;
  low: number;
  byCheck: { check: CheckKey; label: string; count: number }[];
}

export function summarise(findings: Finding[]): AnomalySummary {
  const counts = new Map<CheckKey, number>();
  for (const f of findings) {
    counts.set(f.check, (counts.get(f.check) ?? 0) + (f.count ?? 1));
  }
  return {
    total: findings.reduce((a, f) => a + (f.count ?? 1), 0),
    high: findings.filter((f) => f.severity === 'high').length,
    medium: findings.filter((f) => f.severity === 'medium').length,
    low: findings.filter((f) => f.severity === 'low').length,
    byCheck: [...counts.entries()]
      .map(([check, count]) => ({ check, label: CHECK_LABEL[check], count }))
      .sort((a, b) => b.count - a.count),
  };
}

/**
 * The checks this system cannot run yet, and what each needs.
 *
 * Declared rather than omitted. Segregation of duties, shared supplier bank
 * accounts and payment-run tampering are the checks that matter most in a
 * school, and a page that quietly leaves them out implies a clean bill of
 * health it has not earned.
 */
export const AWAITING_CHECKS: { label: string; needs: string }[] = [
  { label: 'A purchase paid without a matching order and delivery', needs: 'the Purchase Orders module' },
  { label: 'Stock written off without a count behind it', needs: 'the Uniform Shop module' },
  { label: 'Petty cash reimbursed above the float', needs: 'the Petty Cash module' },
];
